import { ShipMessage } from '../types/ShipMessage';
import { PortPosition } from '../types/PortPosition';
import { ShipPosition } from '../types/ShipPosition';
import finalPositions from "./finalPositions.json";
import topTenFastestShips from "./topTenFastestShips.json";
import topTenFuelConsumption from "./topTenFuelConsumption.json";
import messages from "./messages.json";
import ports from "./ports.json";

const USE_LOCAL_MOCK = false;

const API_URL = process.env.REACT_APP_API_URL; // API Gateway base URL, e.g. https://abc123.execute-api.us-east-1.amazonaws.com

// Fetch a private file through the auth proxy Lambda
async function fetchAuthenticated(filename: string, token: string): Promise<any[]> {
  if (!API_URL) throw new Error('REACT_APP_API_URL is not set');
  const response = await fetch(`${API_URL}/data/${filename}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(`Auth proxy error ${response.status}: ${err.error || 'Unknown error'}`);
  }
  return response.json();
}

// --- Authenticated (signed-in users) ---

export async function getFinalPositions(token: string) {
  if (USE_LOCAL_MOCK) return finalPositions as ShipPosition[];
  return fetchAuthenticated('finalPositions.json', token) as Promise<ShipPosition[]>;
}

export async function getTopTenFastestShips(token: string) {
  if (USE_LOCAL_MOCK) return topTenFastestShips as ShipPosition[];
  return fetchAuthenticated('topTenFastestShips.json', token) as Promise<ShipPosition[]>;
}

export async function getTopTenFuelConsumption(token: string) {
  if (USE_LOCAL_MOCK) return topTenFuelConsumption as ShipPosition[];
  return fetchAuthenticated('topTenFuelConsumption.json', token) as Promise<ShipPosition[]>;
}

export async function getMessages(token: string) {
  if (USE_LOCAL_MOCK) return messages as ShipMessage[];
  return fetchAuthenticated('messages.json', token) as Promise<ShipMessage[]>;
}

// --- Public (unauthenticated users) ---
// Positions only — no fuel/speed data.
// Falls back to stripping the full file client-side until finalPositions_public.json exists on S3.

export async function getFinalPositionsPublic() {
  if (USE_LOCAL_MOCK) {
    return finalPositions.map(({ LINE, SHIPNAME, HEADING, LAT, LON, NEXT_PORT_NAME }) => ({
      LINE, SHIPNAME, HEADING, LAT, LON, NEXT_PORT_NAME,
    }));
  }
  try {
    const response = await fetch('https://cruise-map-data.s3.us-east-1.amazonaws.com/finalPositions_public.json');
    if (!response.ok) throw new Error('Public file not found');
    const data: ShipPosition[] = await response.json();
    return data;
  } catch {
    // Fallback: strip fuel fields from full file client-side
    const response = await fetch('https://cruise-map-data.s3.us-east-1.amazonaws.com/finalPositions.json');
    const data: ShipPosition[] = await response.json();
    return data.map(({ LINE, SHIPNAME, HEADING, LAT, LON, NEXT_PORT_NAME }) => ({
      LINE, SHIPNAME, HEADING, LAT, LON, NEXT_PORT_NAME,
    }));
  }
}

// --- Shared (all users) ---

export async function getPortPositions() {
  if (USE_LOCAL_MOCK) return ports as PortPosition[];
  const response = await fetch('https://cruise-map-data.s3.us-east-1.amazonaws.com/ports.json');
  const data: PortPosition[] = await response.json();
  return data;
}
